
// UI COMPONENTS
import { Button, Container, Dropdown, Form, Table } from "react-bootstrap";
// HOOKS & ICONS
import { BsArrowLeft, BsPencil, BsTrash } from "react-icons/bs";
import { Controller, useForm } from "react-hook-form";
// VALIDATION
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
// HELPERS
import { customStyles, formatDate, handleExportData } from "../../../../Utils/helpers/commonhelper";

import { toast } from "sonner";
//css file
import "./ProductServices.css";
import { TableSkeleton } from "../../../../Utils/skeletonLoader/TableSkeleton";
// API handlers
import {
  DeleteRequest,
  GetRequest,
  PutRequest,
} from "../../../../Utils/hooks/ApiHandler";
import DeleteModal from "../../../DeleteModal";
import useLocalStorageState from "use-local-storage-state";
import {
  useGetRequest,
  usePostRequest,
} from "../../../../Utils/hooks/QueryHandler";
import { useGetEssentials } from "../../../../Utils/hooks/useGetEssentials";
import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Select from "react-select";
import nodata from "../../../../Utils/assets/img/leads_no_data_found.png";
import { DescriptionTooltip } from "../../../../Utils/helpers/descriptionTooltip/DescriptionTooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport, faFileImport } from "@fortawesome/free-solid-svg-icons";
import masterImage from "../../../../Utils/assets/img/masterImage.png";
// ---------- Description rich-text editor (local to this file) ----------
// Defined outside the component so Quill doesn't re-initialise on every render
const DESC_QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["clean"],
  ],
};
const DESC_QUILL_FORMATS = ["bold", "italic", "underline", "link", "list", "bullet"];

const escapeHtml = (str) =>
  str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Old products have plain-text descriptions - keep their line breaks inside Quill
const toEditorHtml = (val) => {
  if (!val) return "";
  if (/<\/?[a-z][\s\S]*?>/i.test(val)) return val; // already HTML
  return val
    .split(/\r?\n/)
    .map((line) => `<p>${line ? escapeHtml(line) : "<br>"}</p>`)
    .join("");
};

// Scoped CSS: every selector starts with .product-desc-quill so nothing else in the app is affected
const DESC_QUILL_CSS = `
/* Box mimics the app's floating-label inputs: same border colour, label sits in the top 24px */
.product-desc-quill {
  width: 100%;
  background: #fff;
  border: 1px solid #e2d4dc;
  border-radius: 4px;
  padding-top: 24px;
}
.product-desc-quill:focus-within {
  border-color: var(--primary-color, #e2d4dc);
}
.product-desc-quill .quill { width: 100%; }
.product-desc-quill .ql-toolbar.ql-snow {
  border: 0;
  border-bottom: 1px solid #efe6ec;
  padding: 2px 8px 4px;
  background: #fff;
}
.product-desc-quill .ql-container.ql-snow {
  border: 0;
  font-family: inherit;
  font-size: 0.875rem;
}
.product-desc-quill .ql-editor {
  min-height: 80px;
  padding: 8px 11px;
  font-size: 0.875rem;
  line-height: 1.5;
  text-align: left;
}
.product-desc-quill .ql-editor.ql-blank::before {
  left: 11px;
  right: 11px;
  font-style: normal;
  color: #9ca3af;
}
.product-desc-quill .ql-editor p,
.product-desc-quill .ql-editor ol,
.product-desc-quill .ql-editor ul { margin: 0; }
`;

// String in (value) -> string out (onChange), so it plugs straight into react-hook-form's Controller
const DescriptionEditor = ({ value, onChange }) => {
  const [inner, setInner] = useState(toEditorHtml(value));
  const lastEmitted = useRef(value ?? "");

  // Sync when the form changes the value from outside (reset() after save / on edit)
  useEffect(() => {
    const incoming = value ?? "";
    if (incoming !== lastEmitted.current) {
      lastEmitted.current = incoming;
      setInner(toEditorHtml(incoming));
    }
  }, [value]);

  const handleChange = (html, _delta, source, editor) => {
    if (source !== "user") return;
    setInner(html);
    // Quill gives "<p><br></p>" when empty - store "" instead
    const isEmpty = editor.getText().trim().length === 0 && !/<img/i.test(html);
    const out = isEmpty ? "" : html;
    lastEmitted.current = out;
    onChange(out);
  };

  return (
    <>
      <style>{DESC_QUILL_CSS}</style>
      <div className="product-desc-quill">
        <ReactQuill
          theme="snow"
          value={inner}
          onChange={handleChange}
          modules={DESC_QUILL_MODULES}
          formats={DESC_QUILL_FORMATS}
        />
      </div>
    </>
  );
};
// -----------------------------------------------------------------------

//Form validation schema using Zod
const schema = z.object({
  service: z.string().min(1, "Product name is required"),
  price: z
    .string()
    .refine((val) => /^[0-9]*\.?[0-9]*$/.test(val), {
      message: "Only numbers and one decimal point allowed",
    })
    .refine((val) => val.length <= 8, {
      message: "Price must be at most 8 characters long",
    }),

  tax: z.enum(["0", "5", "18", "40"], {
    required_error: "Tax is required",
  }),
  description: z.string().optional(),
  hsn_sac: z.union([
    z.literal(""),
    z.string().min(6, "HSN/SAC code must be 6 to 8 digits").max(8, "HSN/SAC code must be 6 to 8 digits"),
  ]),
  category: z.enum(["Product", "Service"]).optional(),
  unit: z.string().optional(), // Make it a regular string
})
  .superRefine((data, ctx) => {
    // Custom validation: unit is required only for Product
    if (data.category === "Product" && (!data.unit || data.unit.trim() === "")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Unit is required when category is Product",
        path: ["unit"],
      });
    }
  });
export const ProductServices = () => {
  const { customer, headers, extras, navigate, customerId, loginUserId } =
    useGetEssentials();

  //  Local states for managing designation data, errors, and loading state

  const [products, setProducts] = useState([]);
  const [editId, setEditId] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // delete modal loading state
  const [loading, setLoading] = useState(false)

  //React Hook Form setup
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(schema),

    defaultValues: {
      service: "",
      price: "",
      tax: "5",
      description: "",
      hsn_sac: "",
      category: "Product",
      unit: "",
    },
  });

  // rbac permission object
  const [PERMISSION, _] = useLocalStorageState('permission')

  // master permissions 
  const masterGrant = PERMISSION.setting.feature.master;

  const productsImportFields = [
    { id: "title", label: "Product & Services", required: true },
    { id: "price", label: "Price", required: true },
    { id: "tax", label: "Tax", required: true },
    { id: "description", label: "Description", required: false },
    { id: "hsn_code", label: "HSC_SAC", required: false },
    { id: "category", label: "Category", required: false },
    { id: "unit", label: "Unit", required: false },
  ];
  //Fetch designations when component mounts
  const {
    data: ProductData,
    error,
    refetch: fetchProducts,
    isLoading,

    isError,
  } = useGetRequest(
    `/masters/getProducts/${customerId}`,
    "getProducts",

    headers,
    extras
  );
  // Fetch departments on component mount
  useEffect(() => {
    if (ProductData?.status === "success") {
      setProducts(ProductData.data);
    } else if (isError) {
      console.error("Error fetching roles:", error);
    }
  }, [ProductData, isError]);

  //Add or update designation

  const { mutate: addProduct } = usePostRequest(
    `/masters/createProduct`,
    null,
    { headers },
    extras
  );

  const onSubmit = async (formData) => {
    if (editId === null) {
      if (!masterGrant?.isCreate) {
        toast.warning("You are not authorized for this action!")
        return
      }
    } else if (!masterGrant?.isEdit) {
      toast.warning("You are not authorized for this action!")
      return
    }
    const service = formData.service.trim();
    const description = formData.description?.trim() || "";
    const tax = formData.tax || "";
    const unit = formData.unit || "";
    const hsn_sac = formData.hsn_sac || "";
    const category = formData.category || "";
    const price = formData.price || "";
    //Check for duplicate
    const isDuplicate = products.some(
      (p) => p.service.trim() === service && p.id !== editId
    );
    if (isDuplicate) {
      toast.error("Product name must be unique")
      return
    }

    try {
      const payload = {
        customer_id: customerId,
        service,
        tax,
        unit,
        price,
        hsn_sac,
        description,
        category,
        login_user_id: loginUserId,
      };

      let res;
      if (editId !== null) {
        payload.service_id = editId;

        res = await PutRequest(
          `/masters/updateProduct`,
          payload,
          { headers },
          extras
        );
      } else {
        addProduct(payload, {
          onSuccess: (res) => {
            if (res?.status === "success") {
              toast.success(res.message || "Product added successfully");
              fetchProducts();

              reset({
                tax: "5",
                category: "Product",
                price: "",
                hsn_sac: "",
                unit: "",
                description: "",
                service: "",

              });
            } else {
              toast.error(res.message || "Failed to add product");
            }
          },
          onError: (err) => {
            toast.error(err?.message || "Add product request failed");
          },
        });
      }

      if (res?.status === "success") {
        fetchProducts();
        if (editId !== null) setEditId(null);
        reset({
          tax: "5",
          category: "Product",
          price: "",
          hsn_sac: "",
          unit: "",
          description: "",
          service: "",

        });
        toast.success(res.message);
      } else if (editId !== null && res) {
        toast.error(res.message || "Failed to update product");
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Something went wrong while saving.");
    } finally {
    }
  };

  //Set form data in edit mode
  const onEdit = async (item) => {
    try {
      const res = await GetRequest(
        `masters/getProductById/${item.id}`,
        { headers },
        extras
      );
      if (res?.status === "success" && Array.isArray(res.data)) {
        const data = res.data[0];
        setEditId(item.id);
        reset({
          service: data.service || "",
          description: data.description || "",
          price: data.price || "",
          tax: data.tax || "",
          hsn_sac: data.hsn_sac || "",
          category: data.category || "",
          unit: data.unit || "",
        });
      } else {
        throw new Error(res.message || "Invalid response");
      }
    } catch (error) {
      console.error("Error loading details:", error);
      toast.error("Failed to load product details");
    }
  };
  //Open delete confirmation modal
  const onDelete = (p) => {
    setProductToDelete(p);
    setShowDeleteModal(true);
  };
  //Handle confirmed delete
  const handleConfirmedDelete = async () => {
    if (!productToDelete) return;
    setLoading(true)
    try {
      const res = await DeleteRequest(
        `masters/deleteProduct/${productToDelete.id}`,
        { headers },
        extras
      );

      if (res?.status === "success") {
        const updateProduct = products.filter(
          (p) => p.id !== productToDelete.id
        );
        setProducts(updateProduct);
        await fetchProducts();
        setLoading(false);
        toast.success(res.message);
        setShowDeleteModal(false);
        setProductToDelete(null);
      } else {
        setLoading(false)
        toast.error(res.message);
        // throw new Error(res.message || "Deletion failed");
      }
    } catch (error) {
      setLoading(false)
      console.error("Delete error:", error);
      toast.error("Something went wrong while deleting");
    }
  };

  return (
    <div className="container-fluid p-3 product-container mb-3">
      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmedDelete}
        isLoading={loading}
        message="Are you sure you want to delete this item?"
      />

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div style={{ width: "80%", paddingRight: "20px" }}>
          <h5 className="fw-semibold mb-1">Product And Services</h5>
          <p className="text-muted small">
            Record and Track your business expenditures and get the custom
            reports.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            className="btn btn-default-outline"
            onClick={() => navigate(-1)}
          >
            <BsArrowLeft className="me-1" /> Back
          </Button>
          <Dropdown>
            <Dropdown.Toggle className=" btn btn-default no-arrow" style={{ display: "flex", alignItems: "center", paddingRight: "7px" }}>
              Action
              {/* <i className="bi bi-caret-down-fill d-inline-block ps-1" ></i> */}
              <i className="bx bx-chevron-down" style={{ fontSize: "21px" }}></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  navigate("/importdata", {
                    state: {
                      module_name: "master_product",
                      fieldData: [
                        {
                          title: "General Fields",
                          fields: productsImportFields.map((c) => c),
                        },
                      ],
                    },
                  });
                }}><FontAwesomeIcon
                  icon={faFileImport}
                  style={{
                    width: "16px",
                    height: "16px",
                    marginRight: "9px",
                    color: "#5e5f60",
                  }}
                />{" "}
                Import Data</Dropdown.Item>
              <Dropdown.Item onClick={() => {
                handleExportData([], "master_product", headers, extras);
              }}> <FontAwesomeIcon
                  icon={faFileExport}
                  style={{
                    width: "16px",
                    height: "16px",
                    marginRight: "9px",
                    color: "#5e5f60",
                  }}
                />{" "}
                Export Data</Dropdown.Item>
              {/* <Dropdown.Item>Copy from Other</Dropdown.Item>
              <Dropdown.Item>SVC Data</Dropdown.Item> */}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <hr
        className="mb-4"
        style={{ borderTop: "0.5px solid #6c757d", opacity: "0.3", marginTop: "0.7rem" }}
      />

      <div className="row">
        {/* Form Section */}
        <div className="col-md-4 mb-4">
          <div className="form-card">
            <div className="mb-3">
              <h6 className="fw-semibold mb-1">
                {editId ? "Edit Product" : "Add Product"}
              </h6>
              <p className="text-muted small mb-2">
                {editId
                  ? "Update the products & services details below."
                  : "Add new product & services here."}
              </p>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="form-container">
              <div className="mb-3   d-flex flex-column focus-z-index position-relative">
                <label className="form-label">
                  Enter Product Name <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  placeholder="Enter product name"
                  {...register("service")}
                />
                {errors.service && (
                  <small className="text-danger">
                    {errors.service.message}
                  </small>
                )}
              </div>

              <div className="mb-3  d-flex flex-column focus-z-index position-relative">
                <label className="form-label">Enter Product Price</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control"
                  {...register("price")}
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Delete",
                    ]
                    const isNumber = /^[0-9]$/.test(e.key)
                    const isDot = e.key === "."
                    const alreadyHasDot = e.currentTarget.value.includes(".")

                    if (
                      !allowedKeys.includes(e.key) &&
                      !isNumber &&
                      !(isDot && !alreadyHasDot)
                    ) {
                      e.preventDefault()
                    }
                  }}
                />

                {errors.price && (
                  <small className="text-danger">{errors.price.message}</small>
                )}
              </div>

              {
                customer?.isRegistered &&
                <div className="mb-3  d-flex flex-column focus-z-index position-relative">
                  <label className="form-label">Tax</label>
                  <Controller
                    name="tax"
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={[
                          { value: "0", label: "0%" },
                          { value: "5", label: "5%" },
                          { value: "18", label: "18%" },
                          { value: "40", label: "40%" },
                        ]}
                        styles={customStyles}
                        classNamePrefix="react-select"
                        placeholder="Select tax"
                        isClearable={true} // user can click "X", but...
                        value={
                          field.value
                            ? { value: field.value, label: `${field.value}%` }
                            : { value: "5", label: "5%" } //  if cleared, fallback to 5%
                        }
                        onChange={(selectedOption) => {
                          field.onChange(selectedOption?.value ?? "5") // fallback to 5%
                        }}
                      />
                    )}
                  />
                  {errors.tax && (
                    <small className="text-danger">{errors.tax.message}</small>
                  )}
                </div>
              }

              <div className="mb-3 d-flex flex-column focus-z-index position-relative">
                <label className="form-label textareaLables">Description</label>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <DescriptionEditor
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>

              <div className="mb-3  d-flex flex-column focus-z-index position-relative">
                <label className="form-label">HSN or SAC Code</label>
                <input
                  type="text"
                  className="form-control"
                  {...register("hsn_sac")}
                />
                {errors.hsn_sac && (
                  <small className="text-danger">
                    {errors.hsn_sac.message}
                  </small>
                )}
              </div>

              <div className="mb-3  d-flex flex-column">
                {/* <label className="form-label d-block">Category</label> */}

                <span
                  style={{
                    color: "#878b8e",
                    fontSize: "0.86rem",
                    fontWeight: "400",
                    marginBottom: "7px",
                  }}
                >
                  Category
                </span>
                <div className="d-flex gap-4">
                  <div className="d-flex align-items-end gap-2">
                    <Form.Check
                      type="radio"
                      value="Product"
                      id="category-product"
                      {...register("category")}
                      className="custom-radio"
                      style={{ cursor: "pointer" }}
                    />
                    <style jsx>
                      {`
                      .custom-radio .form-check-input {
                        width: 24px;
                        height: 24px;
                        background-color: var(--custom-white);
                        border: 1px solid var(--input-border);
                      }
                      .custom-radio .form-check-input:checked {
                        width: 24px;
                        height: 24px;
                        background-color: var(--primary-color);
                        border-color: var(--primary-color);
                      }
                    `}
                    </style>
                    <span
                      className=""
                      style={{
                        color: "#878b8e",
                        fontSize: "0.86rem",
                        fontWeight: "400",
                      }}
                    >
                      Product
                    </span>
                  </div>

                  <div className="d-flex align-items-end gap-2  ">

                    <Form.Check
                      type="radio"
                      value="Service"
                      id="category-service"
                      {...register("category")}
                      className="custom-radio"
                      style={{ cursor: "pointer" }}
                    />
                    <span
                      style={{
                        color: "#878b8e",
                        fontSize: "0.86rem",
                        fontWeight: "400",
                      }}
                    >
                      Service
                    </span>
                  </div>
                </div>

                {errors.category && (
                  <small className="text-danger d-block">select category</small>
                )}
              </div>
              <div className="mb-3  d-flex flex-column focus-z-index position-relative">
                <label className="form-label">
                  Units
                  {/* Conditionally show asterisk only when category is Product */}
                  {watch("category") === "Product" && <span className="text-danger">*</span>}
                </label>
                <Controller
                  name="unit"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={[
                        { value: "Kilogram(Kgs)", label: "Kilogram(Kgs)" },
                        { value: "Grams(Gms)", label: "Grams(Gms)" },
                        { value: "Liters(L)", label: "Liters(L)" },
                        { value: "Milliliters(Ml)", label: "Milliliters(Ml)" },
                        { value: "Meters(M)", label: "Meters(M)" },
                        { value: "Centimeters(Cm)", label: "Centimeters(Cm)" },
                        { value: "Dozen(Dz)", label: "Dozen(Dz)" },
                        { value: "Pounds(Lb)", label: "Pounds(Lb)" },
                        { value: "Feet(Ft)", label: "Feet(Ft)" },
                        { value: "Inches(In)", label: "Inches(In)" },
                        { value: "Kilometer(Km)", label: "Kilometer(Km)" },
                        { value: "Box(B)", label: "Box(B)" },
                        { value: "Pieces(P)", label: "Pieces(P)" },
                      ]}
                      styles={customStyles}
                      classNamePrefix="react-select"
                      placeholder="Select unit"
                      isClearable={true}
                      value={
                        field.value
                          ? { value: field.value, label: field.value }
                          : null
                      }
                      onChange={(selectedOption) => {
                        field.onChange(selectedOption?.value ?? "")
                      }}
                    />
                  )}
                />
                {errors.unit && (
                  <small className="text-danger">
                    Please select a valid unit.
                  </small>
                )}
              </div>
              <div className="d-flex gap-2" >
                {editId &&
                  < Button
                    onClick={() => {
                      reset({
                        tax: "5",
                        category: "Product",
                        price: "",
                        hsn_sac: "",
                        unit: "",
                        description: "",
                        service: "",

                      });
                      setEditId(null)
                    }}
                    type="button"
                    className="w-100 btn btn-default-outline">
                    Cancel
                  </Button>
                }
                <Button type="submit" className="w-100 btn btn-default">
                  {editId ? "Update" : "Submit"}
                </Button>
              </div>
            </form>
            <hr className="my-3" />
            <div className="text-center mt-4">
              <img
                src={masterImage}
                alt="illustration"
                className="img-fluid"
                style={{ maxHeight: "180px" }}
              />
              <div className="mt-2">
                <h6 className="fw-semibold mb-1">Product And Services</h6>
                <p className="text-muted  mb-0 small ">
                  Record and Track your business expenditures and get the custom
                  reports.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}

        <div className="col-md-8">
          <div className="table-card">
            <div className="d-flex justify-content-between align-items-start align-items-md-center flex-column flex-md-row">
              <div>
                <h6 className="fw-semibold mb-1">Product And Services</h6>
                <p className="text-muted small mb-2 mb-md-0">
                  List of current products and services within your
                  organization.
                </p>
              </div>
              {/* <div className="d-flex align-items-center gap-2 mt-1 mb-2 mt-md-0 border border-1 rounded-2 px-2"> */}
              <div className=" position-relative mt-1 mb-2 mt-md-0">
                <input
                  disabled={!masterGrant?.isView}
                  type="search"
                  className="form-control "
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    fontSize: "12px",
                    paddingLeft: "36px",
                    height: "35px",
                  }}
                />
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    marginTop: "-26px",
                    zIndex: 1,
                    marginLeft: "13px",
                  }}
                ></i>
              </div>
            </div>

            {error ? (
              <div className="alert alert-danger" role="alert">
                {error?.message || "Something went wrong"}
              </div>
            ) : (
              <>
                {
                  masterGrant?.isView ?
                    <Container
                      fluid
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        height: "calc(100vh - 10rem)",
                        backgroundColor: "white",
                        padding: 0,
                        animation: "cardEntry 1s ease-out forwards",
                      }}
                      className="mt-3 p-0"
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          width: "100%",
                        }}
                      >

                        <div
                          className="listStyleForProductandService container-table"
                          style={{
                            flex: products?.length > 10 ? 1 : "auto",
                            overflow: "auto",
                            width: "100%",
                            maxHeight: products?.length > 10 ? "100%" : "auto",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.scrollbarColor = "#e0e0e0 white"

                          }}
                        >
                          <Table
                            style={{
                              width: "100%",
                              tableLayout: "auto",
                              backgroundColor: "white",
                              marginBottom: "0px",
                            }}
                          >
                            <thead style={{ zIndex: "10" }}>
                              <tr>
                                <th>S.No</th>
                                <th>Title</th>
                                <th>Type</th>
                                <th>Price</th>
                                <th>HSN/SAC</th>
                                <th>Unit</th>
                                <th>Entry Date</th>
                                <th>ACTION</th>
                              </tr>
                            </thead>

                            <tbody>
                              {isLoading ? <TableSkeleton columnCount={8} /> :
                                <>
                                  {products
                                    ?.filter((p) =>
                                      (p.service || "")
                                        .toLowerCase()
                                        .includes(searchTerm.trim().toLowerCase())
                                    )
                                    .map((p, idx) => (
                                      <tr key={p?.id}>
                                        <td>{idx + 1}</td>
                                        <td className="td_position">
                                          <DescriptionTooltip
                                            text={p.service}
                                            maxLength={20}
                                          />
                                        </td>

                                        <td>{p.category}</td>

                                        <td>{p.price}</td>
                                        <td>{p.hsn_sac}</td>
                                        <td>{p.unit ? p.unit : "-"}</td>
                                        <td>{formatDate(p?.created_at)}</td>
                                        <td>
                                          <div className="d-flex gap-2 me-2">
                                            <Button
                                              size="sm"
                                              variant="outline-primary"
                                              onClick={() => {
                                                if (!masterGrant?.isEdit) {
                                                  toast.warning("You are not authorized for this action!")
                                                  return
                                                }
                                                onEdit(p)
                                              }
                                              }
                                              title="Edit"
                                            >
                                              <BsPencil />
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline-danger"
                                              title="Delete"
                                              onClick={() => {
                                                if (!masterGrant?.isDelete) {
                                                  toast.warning("You are not authorized for this action!")
                                                  return
                                                }
                                                onDelete(p)
                                              }}
                                            >
                                              <BsTrash />
                                            </Button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  {/*  if no data found on search then it will run  */}
                                  {products.filter((p) =>
                                    (p.service || "")
                                      .toLowerCase()
                                      .includes(searchTerm.trim().toLowerCase())
                                  ).length === 0 && (
                                      <td
                                        colSpan="14"
                                        style={{ textAlign: "center", color: "gray" }}
                                      >
                                        <div
                                          className="d-flex justify-content-center align-items-center"
                                          style={{ position: "absolute", left: " 30%" }}
                                        >
                                          <img
                                            src={nodata}
                                            alt=""
                                            style={{ width: "17rem", marginTop: "0" }}
                                          />
                                        </div>
                                      </td>
                                    )}
                                </>
                              }
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Container>
                    :
                    <>You are not authorized to view Products and Services listing!</>
                }

              </>
            )}
          </div>
        </div>
      </div>
    </div >
  )
};